import { useEffect, useState } from 'react';
import { MapPin, Navigation, Home, Briefcase, X, ChevronDown, Plus, Map, Edit, Trash2 } from 'lucide-react';
import { MapAddressSelector } from './MapAddressSelector';
import api from '../../lib/api';

interface SavedAddress {
  id: string;
  type: 'Home' | 'Work' | 'Other';
  label?: string;
  address: string;
  landmark?: string;
  isPrimary?: boolean;
  lat?: number;
  lng?: number;
}

interface AddressDetails {
  formattedAddress: string;
  street?: string;
  area?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  lat: number;
  lng: number;
  landmark?: string;
  houseNumber?: string;
  floor?: string;
  addressType?: 'Home' | 'Work' | 'Other';
  addressLabel?: string;
}

interface LocationSelectorProps {
  currentLocation: string;
  onLocationChange: (location: string) => void;
  variant?: 'default' | 'header';
}

export function LocationSelector({ currentLocation, onLocationChange, variant = 'default' }: LocationSelectorProps) {
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [pendingMapOpen, setPendingMapOpen] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);

  const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);

  const persistUserCoords = (lat?: number, lng?: number) => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    localStorage.setItem('customer_latitude', String(lat));
    localStorage.setItem('customer_longitude', String(lng));
    localStorage.setItem('customer_location_updated_at', String(Date.now()));
  };

  const fetchSavedAddresses = async () => {
    const hasToken = Boolean(localStorage.getItem('auth_token') || localStorage.getItem('token'));
    if (!hasToken) {
      setSavedAddresses([]);
      return;
    }
    try {
      const response = await api.users.getSavedLocations();
      const rows = Array.isArray(response?.data) ? response.data : [];
      const mapped: SavedAddress[] = rows.map((item) => {
        const rawType = String(item.addressType || 'HOME').toUpperCase();
        const type: SavedAddress['type'] =
          rawType === 'OFFICE' ? 'Work' : rawType === 'OTHER' ? 'Other' : 'Home';
        return {
          id: item.id,
          type,
          label: item.label || type,
          address: item.fullAddress,
          landmark: item.deliveryInstructions || undefined,
          isPrimary: Boolean(item.isDefault),
          lat: typeof item.latitude === 'number' ? item.latitude : undefined,
          lng: typeof item.longitude === 'number' ? item.longitude : undefined,
        };
      });
      setSavedAddresses(mapped);
    } catch {
      // Keep UI usable even if API fails.
    }
  };

  const handleUseCurrentLocation = () => {
    setDetectingLocation(true);
    
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      setDetectingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
          .then(response => response.json())
          .then(data => {
            const address = data.display_name || `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`;
            persistUserCoords(latitude, longitude);
            onLocationChange(`Current Location: ${address}`);
            setDetectingLocation(false);
            setShowLocationModal(false);
          })
          .catch(() => {
            persistUserCoords(latitude, longitude);
            onLocationChange(`Current Location: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
            setDetectingLocation(false);
            setShowLocationModal(false);
          });
      },
      (error) => {
        setDetectingLocation(false);
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            alert('Location access denied. Please enable location permissions in your browser settings.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('Location information is unavailable. Please try again.');
            break;
          case error.TIMEOUT:
            alert('Location request timed out. Please try again.');
            break;
          default:
            alert('An unknown error occurred while getting your location.');
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSelectAddress = (address: SavedAddress) => {
    persistUserCoords(address.lat, address.lng);
    onLocationChange(`${address.type}: ${address.address}`);
    setShowLocationModal(false);
  };

  const handleEditAddress = (address: SavedAddress) => {
    setEditingAddressId(address.id);
    setShowMapSelector(true);
  };

  const handleDeleteAddress = (addressId: string) => {
    const target = savedAddresses.find((item) => item.id === addressId);
    if (!target) return;

    const confirmed = window.confirm(`Delete ${target.type} address?`);
    if (!confirmed) return;

    let next = savedAddresses.filter((item) => item.id !== addressId);
    if (target.isPrimary && next.length > 0 && !next.some((item) => item.isPrimary)) {
      next = next.map((item, index) => (index === 0 ? { ...item, isPrimary: true } : item));
    }
    setSavedAddresses(next);
  };

  useEffect(() => {
    if (!showLocationModal && pendingMapOpen) {
      setShowMapSelector(true);
      setPendingMapOpen(false);
    }
  }, [showLocationModal, pendingMapOpen]);

  useEffect(() => {
    if (showLocationModal) {
      fetchSavedAddresses();
    }
  }, [showLocationModal]);

  // Truncate location for display
  const displayLocation = currentLocation.length > 30 
    ? currentLocation.substring(0, 30) + '...' 
    : currentLocation;

  const buttonClassName =
    variant === 'header'
      ? 'flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-md hover:shadow-lg transition-all border border-gray-200 hover:border-[#60A5FA] group w-full sm:w-auto max-w-[340px] sm:max-w-[260px]'
      : 'flex items-center gap-2 bg-white/95 backdrop-blur-sm px-4 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all border border-gray-200 hover:border-[#60A5FA] group w-auto max-w-xs';

  return (
    <>
      {/* Location Button */}
      <button
        type="button"
        onClick={() => setShowLocationModal(true)}
        className={buttonClassName}
      >
        <MapPin className={variant === 'header' ? 'w-4 h-4 text-[#60A5FA] flex-shrink-0' : 'w-5 h-5 text-[#60A5FA] flex-shrink-0'} />
        <div className="flex-1 text-left min-w-0">
          <div className={variant === 'header' ? 'text-[10px] text-gray-500 font-medium leading-none' : 'text-xs text-gray-500 font-medium'}>
            Deliver to
          </div>
          <div className={variant === 'header' ? 'text-xs font-semibold text-gray-900 truncate' : 'text-sm font-semibold text-gray-900 truncate'}>
            {displayLocation}
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-[#60A5FA] flex-shrink-0 transition-colors" />
      </button>

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 rounded-t-3xl">
              <h2 className="text-xl font-bold text-gray-900">Select Location</h2>
              <button
                type="button"
                onClick={() => setShowLocationModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Current Location Button */}
              <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={detectingLocation}
                className="w-full bg-gradient-to-r from-orange-400 to-pink-500 text-white py-4 px-6 rounded-2xl font-semibold hover:shadow-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-3 group disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <Navigation className={`w-6 h-6 ${detectingLocation ? 'animate-spin' : 'group-hover:scale-110 transition-transform'}`} />
                <span className="text-lg">{detectingLocation ? 'Detecting location...' : 'Use Current Location'}</span>
              </button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 bg-white text-gray-500 text-sm font-medium">OR</span>
                </div>
              </div>

              {/* Saved Addresses */}
              <div>
                <h3 className="text-gray-900 font-semibold mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-500" />
                  Saved Addresses
                </h3>
                <div className="space-y-3">
                  {savedAddresses.map((address) => {
                    const Icon = address.type === 'Home' ? Home : address.type === 'Work' ? Briefcase : MapPin;
                    const iconColor = address.type === 'Home' ? 'text-blue-500' : address.type === 'Work' ? 'text-purple-500' : 'text-pink-500';
                    const iconBg = address.type === 'Home' ? 'bg-blue-50' : address.type === 'Work' ? 'bg-purple-50' : 'bg-pink-50';
                    const hoverBg = address.type === 'Home' ? 'group-hover:bg-blue-100' : address.type === 'Work' ? 'group-hover:bg-purple-100' : 'group-hover:bg-pink-100';
                    const borderHover = address.type === 'Home' ? 'hover:border-blue-400' : address.type === 'Work' ? 'hover:border-purple-400' : 'hover:border-pink-400';
                    
                    return (
                      <div key={address.id} className="relative">
                        <button
                          type="button"
                          onClick={() => handleSelectAddress(address)}
                          className={`w-full text-left p-4 pr-24 rounded-2xl border-2 border-gray-200 ${borderHover} hover:shadow-lg transition-all duration-200 group`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl ${iconBg} ${hoverBg} flex items-center justify-center flex-shrink-0 transition-colors`}>
                              <Icon className={`w-6 h-6 ${iconColor} transition-colors`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900">{address.label || address.type}</h4>
                                {address.isPrimary && (
                                  <span className="text-xs bg-gradient-to-r from-green-400 to-emerald-500 text-white px-2 py-0.5 rounded-full font-medium">
                                    Primary
                                  </span>
                                )}
                              </div>
                              <p className="text-gray-600 text-sm leading-relaxed mb-1">{address.address}</p>
                              {address.landmark && (
                                <p className="text-gray-500 text-xs">Near: {address.landmark}</p>
                              )}
                            </div>
                          </div>
                        </button>
                        <div className="absolute top-3 right-3 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAddress(address);
                            }}
                            className="w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors"
                            aria-label={`Edit ${address.type} address`}
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteAddress(address.id);
                            }}
                            className="w-8 h-8 rounded-full bg-red-100 hover:bg-red-200 flex items-center justify-center transition-colors"
                            aria-label={`Delete ${address.type} address`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add New Address Button */}
              <button
                onClick={() => {
                  setEditingAddressId(null);
                  setShowLocationModal(false);
                  setPendingMapOpen(true);
                }}
                type="button"
                className="w-full py-3 px-6 border-2 border-dashed border-gray-300 rounded-2xl text-gray-600 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-all duration-200 flex items-center justify-center gap-2 font-medium"
              >
                <Plus className="w-5 h-5" />
                <span>Add New Address</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Address Selector */}
      {showMapSelector && (
        <MapAddressSelector
          isOpen={showMapSelector}
          onClose={() => setShowMapSelector(false)}
          initialLocation={
            editingAddressId
              ? (() => {
                  const addr = savedAddresses.find((item) => item.id === editingAddressId);
                  return addr?.lat !== undefined && addr?.lng !== undefined
                    ? { lat: addr.lat, lng: addr.lng }
                    : undefined;
                })()
              : undefined
          }
          onSelectAddress={(addressDetails: AddressDetails) => {
            // Format the address with all details
            let formattedAddress = '';
            
            if (addressDetails.houseNumber) {
              formattedAddress += addressDetails.houseNumber;
              if (addressDetails.floor) {
                formattedAddress += `, ${addressDetails.floor}`;
              }
              formattedAddress += ', ';
            }
            
            formattedAddress += addressDetails.formattedAddress;
            
            if (addressDetails.landmark) {
              formattedAddress += ` (Near: ${addressDetails.landmark})`;
            }

            if (editingAddressId) {
              setSavedAddresses((prev) =>
                prev.map((item) =>
                  item.id === editingAddressId
                    ? {
                        ...item,
                        type: addressDetails.addressType || item.type,
                        label: addressDetails.addressLabel || addressDetails.addressType || item.label || item.type,
                        address: formattedAddress,
                        landmark: addressDetails.landmark || undefined,
                        lat: addressDetails.lat,
                        lng: addressDetails.lng,
                      }
                    : item
                )
              );
              setEditingAddressId(null);
            } else {
              persistUserCoords(addressDetails.lat, addressDetails.lng);
              onLocationChange(`${addressDetails.addressType || 'Other'}: ${formattedAddress}`);
              setShowLocationModal(false);
            }
            fetchSavedAddresses();
            setShowMapSelector(false);
          }}
          existingLabels={savedAddresses
            .filter((item) => item.id !== editingAddressId)
            .map((item) => item.label || item.type)}
        />
      )}
    </>
  );
}

